import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import type { User } from "firebase/auth";
import { getIdTokenResult } from "firebase/auth";

import { subscribeToAuth } from "@/lib/firebase";

export type Role = "admin" | "imagepackcreator" | "kioskconsumer";

export type TenantClaim = {
  tenantId: string;
  role: Role;
  raw: string;
};

export type AuthState = {
  user: User | null;
  loading: boolean;
  claim: TenantClaim | null;
  /** Force refresh of Firebase ID token and update claim state */
  refreshClaims: () => Promise<TenantClaim | null>;
};

const AuthContext = createContext<AuthState>({
  user: null,
  loading: true,
  claim: null,
  refreshClaims: async () => null,
});

function parseClaim(raw: unknown): TenantClaim | null {
  if (typeof raw !== "string") return null;
  const [tenantId, role, ...rest] = raw.split("+");
  if (!tenantId || !role || rest.length > 0) return null;
  if (
    role !== "admin" &&
    role !== "imagepackcreator" &&
    role !== "kioskconsumer"
  )
    return null;
  return { tenantId, role, raw };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [claim, setClaim] = useState<TenantClaim | null>(null);

  const refreshClaims = useCallback(async (): Promise<TenantClaim | null> => {
    if (!user) return null;
    try {
      const res = await getIdTokenResult(user, true);
      const parsed = parseClaim(res.claims["b2b_tenant"]);
      setClaim(parsed);
      return parsed;
    } catch {
      return null;
    }
  }, [user]);

  useEffect(() => {
    const unsub = subscribeToAuth(async (u) => {
      setUser(u);
      setLoading(false);

      if (!u) {
        setClaim(null);
        return;
      }

      try {
        const res = await getIdTokenResult(u, true);
        setClaim(parseClaim(res.claims["b2b_tenant"]));
      } catch {
        setClaim(null);
      }
    });
    return () => unsub();
  }, []);

  const value = useMemo<AuthState>(
    () => ({ user, loading, claim, refreshClaims }),
    [user, loading, claim, refreshClaims],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
